<template>
    <!-- Shown under the packages when the chosen location has no stock: one click to be emailed when it is back. -->
    <div class="stock-alert" :class="{ 'is-done': waiting }">
        <template v-if="waiting">
            <v-icon icon="mdi-check-circle-outline" size="20"></v-icon>
            <span class="stock-alert__done">{{ t('stockAlert.done', { email: waiting.email, place }) }}</span>
            <button type="button" class="stock-alert__cancel" @click="store.cancel(waiting.id)">{{ t('stockAlert.cancel') }}</button>
        </template>
        <template v-else>
            <div class="stock-alert__top">
                <span class="stock-alert__bell"><v-icon icon="mdi-bell-ring" size="22"></v-icon></span>
                <div>
                    <p class="stock-alert__title">{{ t('stockAlert.title', { place }) }}</p>
                    <p class="stock-alert__sub">{{ t('stockAlert.sub') }}</p>
                </div>
            </div>
            <form class="stock-alert__row" @submit.prevent="submit">
                <input v-model.trim="email" type="email" autocomplete="email" :placeholder="t('stockAlert.emailPlaceholder')" :aria-label="t('auth.email')" />
                <button type="submit" class="stock-alert__btn" :disabled="store.saving">{{ store.saving ? t('stockAlert.saving') : t('stockAlert.notify') }}</button>
            </form>
            <p v-if="error" class="stock-alert__error">{{ error }}</p>
        </template>
    </div>
</template>

<script setup>
import { computed, defineProps, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { t } from '@/i18n';
import { useAccountStore } from '@/stores';
import { useStockAlertStore } from '@/stores/stockAlert.store';

const props = defineProps({
    country: { type: String, default: '' },
    isp: { type: String, default: '' },
    city: { type: String, default: '' },
    isIpv6: { type: Boolean, default: false },
    place: { type: String, default: '' },     // already translated: "Three · Manchester"
});

const store = useStockAlertStore();
const { user } = storeToRefs(useAccountStore());
const email = ref('');
const error = ref('');

// my waiting request for this exact location, if any
const waiting = computed(() => store.mine.find((a) => a.country === props.country && a.isp === props.isp && a.city === props.city && (a.is_ipv6 === true) === props.isIpv6));

watch(user, (u) => { if (!email.value && u?.email) email.value = u.email; }, { immediate: true });
onMounted(() => { if (!store.loaded) store.loadMine(); });

const submit = async () => {
    error.value = '';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.value)) { error.value = t('stockAlert.invalidEmail'); return; }
    await store.create({ country: props.country, isp: props.isp, city: props.city, isIpv6: props.isIpv6, email: email.value });
};
</script>

<style scoped>
/* deliberately the loudest thing on the page when a location is out of stock: solid brand colour, white text */
.stock-alert { margin-top: 14px; padding: 16px 18px; border-radius: 14px; background: #5750e3; color: #ffffff; box-shadow: 0 0 0 3px rgba(101, 96, 240, 0.35); }
.stock-alert.is-done { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; background: var(--ui-ok); box-shadow: none; color: #ffffff; font-size: 14.5px; font-weight: 600; }
.stock-alert__done { flex: 1; min-width: 200px; }
.stock-alert__cancel { border: 0; background: transparent; color: #ffffff; text-decoration: underline; font-size: 13px; font-weight: 400; cursor: pointer; }
.stock-alert__top { display: flex; align-items: center; gap: 14px; margin-bottom: 12px; }
.stock-alert__bell { flex: none; display: grid; place-items: center; width: 44px; height: 44px; border-radius: 50%; background: #ffc53d; color: #3a2a00; animation: stock-alert-ring 1.2s ease-in-out 0.3s 2; transform-origin: 50% 10%; }
@keyframes stock-alert-ring { 0%, 100% { transform: rotate(0); } 15% { transform: rotate(14deg); } 30% { transform: rotate(-12deg); } 45% { transform: rotate(9deg); } 60% { transform: rotate(-6deg); } 75% { transform: rotate(3deg); } }
@media (prefers-reduced-motion: reduce) { .stock-alert__bell { animation: none; } }
.stock-alert__title { margin: 0 0 2px; font-size: 17px; font-weight: 700; line-height: 1.3; color: #ffffff; }
.stock-alert__sub { margin: 0; font-size: 13.5px; color: rgba(255, 255, 255, 0.85); }
.stock-alert__row { display: flex; flex-wrap: wrap; gap: 8px; }
.stock-alert__row input { flex: 1; min-width: 220px; height: 46px; padding: 0 14px; border: 2px solid transparent; border-radius: 10px; outline: none; background: #ffffff; color: #1f1e33; font-size: 15px; }
.stock-alert__row input:focus { border-color: #ffc53d; }
.stock-alert__row input::placeholder { color: #86849c; }
.stock-alert__btn { height: 46px; padding: 0 24px; border: 0; border-radius: 10px; background: #ffc53d; color: #3a2a00; font-size: 15px; font-weight: 700; cursor: pointer; transition: transform 150ms ease, background-color 150ms ease; }
.stock-alert__btn:hover:not(:disabled) { background: #ffd566; transform: translateY(-1px); }
.stock-alert__btn:disabled { opacity: 0.6; cursor: not-allowed; }
.stock-alert__error { margin: 8px 0 0; font-size: 13px; font-weight: 600; color: #ffe1a6; }
</style>
